import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Box} from '@mui/material';
import {DraftProjectStatus, ProjectType} from '@contentlab/api/project/types';
import FeatureEditor from '@contentlab/components/feature-editor';
import {creditsTooltipTextForFeature} from '@contentlab/components/feature-editor/utils';
import ReplaceVideoButton from '@contentlab/components/replace-video-button';
import ReplaceVideoModal from '@contentlab/components/replace-video-modal';
import SimulatedProgressLoadingScreen from '@contentlab/components/simulated-progress-loading-screen';
import VideoDisplay from '@contentlab/components/video-display';
import {
  DEFAULT_DEBOUNCE_DELAY,
  FEATURE_BEST_PRACTICES_LOCALE_KEY,
  PROJECT_ID_SEARCH_PARAM_KEY,
} from '@contentlab/config/constants';
import {getUploadLimits} from '@contentlab/config/uploadLimits';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useManageDraftProject from '@contentlab/hooks/mutations/use-manage-draft-project/useManageDraftProject';
import useGenerateProject from '@contentlab/hooks/use-generate-project/useGenerateProject';
import useMergedAssetErrors from '@contentlab/hooks/use-merged-asset-errors';
import useOpenPricing from '@contentlab/hooks/use-open-pricing';
import useReadyAssets from '@contentlab/hooks/use-ready-assets';
import contentLabStore, {
  contentLabActions,
  UploadedVideo,
  UploadingFile,
} from '@contentlab/stores/contentLabStore';
import videoPlaybackStore, {
  videoPlaybackActions,
} from '@contentlab/stores/videoPlaybackStore';
import {UploadMode} from '@contentlab/types/UploadMode';
import {Asset} from '@contentlab/types/asset';
import {FeatureName} from '@contentlab/types/project';
import {srtArrayToBase64, SrtItem} from '@contentlab/utils/srtUtils';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {calculateVideoTokensForAssets} from '@/views/content-studio/components/utils';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets';
import {useDebouncedCallback} from '@/hooks/use-debounce';
import useNavigation from '@/hooks/use-navigation';
import EmptyRephraseContent from './components/empty-rephrase-content';
import RephraseContent from './components/rephrase-content';
import RephraseEditor from './components/rephrase-editor';

function ContentDisplay({
  video,
  asset,
  currentIndex,
  uploadingFiles,
  createDropzoneConfig,
}: {
  video: UploadedVideo;
  asset?: Asset;
  currentIndex: string | null;
  uploadingFiles: Record<string, UploadingFile>;
  createDropzoneConfig: (() => object) | null;
}) {
  const [showReplaceVideoModal, setShowReplaceVideoModal] = useState(false);
  const [replaceVideoModalPresentationId, setReplaceVideoModalPresentationId] =
    useState<string>(uuid());
  const {draftRephrasingProject} = contentLabStore(
    useShallow((state) => ({
      draftRephrasingProject: state.draftRephrasingProject,
    }))
  );
  const {isPlaying} = videoPlaybackStore(
    useShallow((state) => ({
      isPlaying: state.isPlaying,
    }))
  );

  const {uiInteraction} = useUiInteractionEvent();
  const {viewPresented} = useViewPresentedEvent();
  const uploadedFile = uploadingFiles[video?.fileName || ''];

  const generateVideoFileUrl = () => {
    if (uploadedFile) {
      return URL.createObjectURL(uploadedFile.file);
    }
    return '';
  };

  // Reset video playback state when component mounts or video changes
  useEffect(() => {
    videoPlaybackActions.reset();
  }, [video?.id, asset?.id]);

  const getSubtitleTime = () => {
    if (
      !draftRephrasingProject?.currentSrt ||
      draftRephrasingProject.currentSrt.length === 0
    ) {
      return 0;
    }

    if (currentIndex) {
      const matchingItem = draftRephrasingProject.currentSrt.find(
        (item: SrtItem) => item.id === currentIndex
      );
      return matchingItem?.startSeconds ?? 0;
    }

    // Default to first segment's start time
    return draftRephrasingProject.currentSrt[0]?.startSeconds ?? 0;
  };

  const videoProps = {
    id: 'rephrase-video-display',
    videoName: asset?.name || video?.fileName || '',
    videoUrl: asset?.url || generateVideoFileUrl(),
    subtitleTime: getSubtitleTime(),
    currentIndex,
    onTimeUpdate: videoPlaybackActions.setCurrentTime,
    isPlayingState: {
      isPlaying,
      setIsPlaying: videoPlaybackActions.setIsPlaying,
    },
  };

  const onPlayToggle = (playing: boolean) => {
    uiInteraction({
      item_name: 'rephrase_video_play',
      interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE_VIDEO_DISPLAY,
      interaction_details: playing ? 'pause' : 'play',
      flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
    });
  };
  const onTimeChange = (time: number, percentage: number) => {
    uiInteraction({
      item_name: 'rephrase_video_display_progress',
      interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE_VIDEO_DISPLAY,
      interaction_details: `${percentage}%`,
      flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
    });
  };

  return (
    <Box height="100%" position="relative">
      <VideoDisplay
        {...videoProps}
        enableKeyboardControls={false}
        onPlayToggle={onPlayToggle}
        onTimeChange={onTimeChange}
        actionComponent={
          <ReplaceVideoButton
            onClick={(event) => {
              event.stopPropagation();
              const interactionId = uuid();
              uiInteraction({
                item_name: 'replace_video',
                interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE,
                flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
                interaction_id: interactionId,
              });
              viewPresented({
                interaction_id: interactionId,
                view_name: CONTENT_LAB_VIEW_NAMES.REPLACE_VIDEO_MODAL,
                presentation_id: replaceVideoModalPresentationId,
                view_type: 'popup',
                view_location: CONTENT_LAB_VIEW_NAMES.REPHRASE,
                source: 'item_pressed',
              });
              setShowReplaceVideoModal(true);
            }}
          />
        }
      />
      <ReplaceVideoModal
        open={showReplaceVideoModal}
        setOpen={setShowReplaceVideoModal}
        presentationId={replaceVideoModalPresentationId}
        setPresentationId={setReplaceVideoModalPresentationId}
        createDropzoneConfig={createDropzoneConfig}
        headlineText={translate('Replacing video will reset your edits.')}
        flowName={FLOW_NAMES.CONTENT_LAB.REPHRASE}
        resetProject={() => {
          contentLabActions.updateDraftRephrasingProjectTranscriptionLoaded(
            false
          );
          contentLabActions.updateDraftRephrasingProjectSrt([]);
          contentLabActions.removeFeatureUploadingFiles(FeatureName.REPHRASING);
          contentLabActions.removeFeatureUploadedVideos(FeatureName.REPHRASING);
          contentLabActions.removeFeatureAssetErrors(FeatureName.REPHRASING);
        }}
      />
    </Box>
  );
}

// using shared ReplaceVideoModal

function RephrasePage() {
  const {draftRephrasingProject} = contentLabStore(
    useShallow((state) => ({
      draftRephrasingProject: state.draftRephrasingProject,
    }))
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: _draftProjectData,
    createDraftProject,
    updateDraftProject,
    resetDraftProject,
    isLoading: isManagingProjectLoading,
  } = useManageDraftProject({
    type: ProjectType.REPHRASING,
    draftProject: draftRephrasingProject,
    projectId: searchParams.get(PROJECT_ID_SEARCH_PARAM_KEY),
    onGetProjectErrorCallback: () => {
      searchParams.delete(PROJECT_ID_SEARCH_PARAM_KEY);
      setSearchParams(searchParams);
    },
  });

  const {
    uploadedVideos,
    uploadingFiles,
    assetErrors: clientAssetErrors,
  } = useFeatureAssets(FeatureName.REPHRASING);

  const {assetErrors, invalidAssetIds, validUploadedVideos} =
    useMergedAssetErrors({
      serverAssetErrors:
        draftRephrasingProject?.validationErrors?.userInput?.assets || [],
      clientAssetErrors,
      uploadedVideos,
    });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldUpdateDraftProject, setShouldUpdateDraftProject] =
    useState(false);
  const {uiInteraction} = useUiInteractionEvent();

  const [currentIndex, setCurrentIndex] = useState<string | null>(null);
  const [createDropzoneConfig, setCreateDropzoneConfig] = useState<
    (() => object) | null
  >(null);
  const openPricing = useOpenPricing();
  const {navigate} = useNavigation();

  const getDraftRephrasingProjectState = () =>
    contentLabStore.getState().draftRephrasingProject;

  const getRephrasingSpec = (asset?: Asset) => {
    const projectState = getDraftRephrasingProjectState();
    const spec = {
      assetId: (asset || readyAsset)?.id || '',
      outputTranscription: srtArrayToBase64(projectState.currentSrt),
      enableLipSync: projectState.enableLipSync,
    };

    // if the uploadedVideos first element does not match the spec assetId then reset it
    if (uploadedVideos[0]?.id !== spec.assetId) {
      spec.assetId = '';
    }

    return spec;
  };

  const createProject = async (asset?: Asset) => {
    if (draftRephrasingProject.isInitialized) {
      return;
    }

    try {
      uiInteraction({
        item_name: 'create_rephrase_project',
        interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE,
        flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
        interaction_id: uuid(),
      });

      await createDraftProject({
        type: ProjectType.REPHRASING,
        projectName: getDraftRephrasingProjectState().projectName,
        draft: {
          rephrasingSpec: getRephrasingSpec(asset),
        },
      });
      setShouldUpdateDraftProject(true);
    } catch (error) {
      // No operation
    }
  };

  const updateProject = async (
    status: DraftProjectStatus = DraftProjectStatus.UPDATE_DRAFT,
    asset?: Asset
  ) => {
    if (!draftRephrasingProject.isInitialized) {
      return;
    }

    uiInteraction({
      item_name: 'update_rephrase_project',
      interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE,
      flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
      interaction_id: uuid(),
    });

    await updateDraftProject({
      status,
      projectName: getDraftRephrasingProjectState().projectName,
      draft: {
        rephrasingSpec: getRephrasingSpec(asset),
      },
    });
  };

  useEffect(() => {
    // update the project to save the transcription after creation
    if (shouldUpdateDraftProject) {
      setShouldUpdateDraftProject(false);
      updateProject();
    }
  }, [shouldUpdateDraftProject]);

  const updateOrCreateProject = async (asset?: Asset) => {
    if (!draftRephrasingProject.isInitialized) {
      await createProject(asset);
    } else {
      await updateProject(DraftProjectStatus.UPDATE_DRAFT, asset);
    }
  };

  const debouncedUpdateOrCreateProject = useDebouncedCallback(
    updateOrCreateProject,
    DEFAULT_DEBOUNCE_DELAY
  );

  const isRephraseProjectEdited = useMemo(() => {
    return !!draftRephrasingProject?.currentSrt.some(
      (srtItem: SrtItem) => srtItem.currentText !== srtItem.originalText
    );
  }, [draftRephrasingProject]);

  const selectedAssetId = useMemo(() => {
    if (uploadedVideos.length === 1) {
      return uploadedVideos[0].id;
    }
    if (draftRephrasingProject) {
      return draftRephrasingProject.assetId;
    }
    return null;
  }, [uploadedVideos, draftRephrasingProject]);

  const readyAssets = useReadyAssets({
    assetIds: selectedAssetId ? [selectedAssetId] : [],
    onReady: (assets) => updateOrCreateProject(assets[0]),
    keepPreviousData: false,
  });

  const [readyAsset] = readyAssets;

  useEffect(() => {
    const [uploadedVideo] = uploadedVideos;
    // immediately clear assets if there is an issue
    // the assetErrors are cleared only after selection of another file
    if (invalidAssetIds.has(uploadedVideo?.id)) {
      contentLabActions.removeFeatureUploadedVideos(FeatureName.REPHRASING);
      contentLabActions.removeFeatureUploadingFiles(FeatureName.REPHRASING);
    }
  }, [uploadedVideos, invalidAssetIds]);

  const isLoading = useMemo(() => {
    if (uploadedVideos.length === 0) {
      return false;
    }
    return !draftRephrasingProject.transcriptionLoaded;
  }, [uploadedVideos, draftRephrasingProject.transcriptionLoaded]);

  const generateProjectDescription = useMemo(() => {
    if (isLoading) {
      return 'Extracting script';
    }
    if (isSubmitting) {
      return '';
    }
    const isUploading = uploadedVideos.some((video) => video.isUploading);
    if (!isUploading && !draftRephrasingProject.transcriptionLoaded) {
      return translate('Upload a video to start editing');
    }
    if (draftRephrasingProject.transcriptionLoaded) {
      return translate('Select a paragraph to edit text - then press generate');
    }
    return '';
  }, [
    uploadedVideos,
    draftRephrasingProject.transcriptionLoaded,
    isLoading,
    isSubmitting,
  ]);

  const renderUploadContent = useCallback(
    ([video]: UploadedVideo[]) => (
      <ContentDisplay
        video={video}
        asset={readyAsset}
        currentIndex={currentIndex}
        uploadingFiles={uploadingFiles}
        createDropzoneConfig={createDropzoneConfig}
      />
    ),
    [readyAsset, currentIndex, uploadingFiles, createDropzoneConfig]
  );

  const onLearnMoreClick = useCallback(() => {
    contentLabActions.setIsLearnMoreModalOpen(true);
    uiInteraction({
      item_name: 'learn more',
      interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE,
    });
  }, []);

  const onFilePickerReady = useCallback(
    (dropzoneConfigFactory: () => object) => {
      setCreateDropzoneConfig(() => dropzoneConfigFactory);
    },
    [setCreateDropzoneConfig]
  );

  const onFilesSelected = useCallback(() => {
    // clear all current asset errors upon file selection
    contentLabActions.removeFeatureAssetErrors(FeatureName.REPHRASING);
  }, []);

  const getRequiredCredits = () => {
    if (uploadedVideos.length === 0) {
      return 0;
    }
    return calculateVideoTokensForAssets(uploadedVideos);
  };

  const generateProject = useGenerateProject({
    getRequiredCredits,
    setIsSubmitting,
    draftProject: draftRephrasingProject,
    defaultProjectName: 'Rescript',
    updateProject,
    resetProject: resetDraftProject,
  });
  const handleGenerateClick = async () => {
    uiInteraction({
      item_name: 'execute_rephrase_project',
      interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE,
      flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
      interaction_id: uuid(),
      interaction_details: JSON.stringify({
        user_edited_project: isRephraseProjectEdited,
      }),
    });
    if (!isRephraseProjectEdited) {
      raiseFlashMessage({
        status: 'error',
        message: translate(
          'Please edit at least one paragraph before generating the video.'
        ),
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await generateProject();
    } catch (error) {
      // No operation
    }
    setIsSubmitting(false);
  };

  const isGenerateButtonDisabled = useMemo(
    () =>
      uploadedVideos.length === 0 ||
      uploadedVideos.some((video) => video.isUploading) ||
      isSubmitting ||
      !draftRephrasingProject.projectName ||
      isManagingProjectLoading ||
      readyAsset?.status !== 'ready',
    [
      uploadedVideos,
      isSubmitting,
      draftRephrasingProject.projectName,
      isManagingProjectLoading,
      readyAsset?.status,
    ]
  );

  const isGenerateButtonLoading = useMemo(
    () =>
      draftRephrasingProject.transcriptionLoaded &&
      (isSubmitting ||
        uploadedVideos.some((video) => video.isUploading) ||
        isLoading ||
        isManagingProjectLoading),
    [
      draftRephrasingProject.transcriptionLoaded,
      isSubmitting,
      uploadedVideos,
      isLoading,
      isManagingProjectLoading,
    ]
  );

  const isFileLoading = useMemo(() => {
    if (isLoading) {
      return true;
    }
    return (
      validUploadedVideos.length > 0 &&
      !draftRephrasingProject.transcriptionLoaded
    );
  }, [
    isLoading,
    validUploadedVideos,
    draftRephrasingProject.transcriptionLoaded,
  ]);

  const getFeatureDetailsComponent = () => {
    if (
      draftRephrasingProject.isInitialized &&
      draftRephrasingProject.transcriptionLoaded
    ) {
      return (
        <RephraseContent
          draftRephrasingProject={draftRephrasingProject}
          lipSyncEnabledChanged={(checked) => {
            uiInteraction({
              item_name: 'lip_sync_toggle',
              interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE,
              flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
              interaction_details: checked ? 'on' : 'off',
            });
            contentLabActions.updateDraftRephrasingProjectLipSync(checked);
            debouncedUpdateOrCreateProject();
          }}
          onLearnModeClick={onLearnMoreClick}
        >
          <RephraseEditor
            projectId={draftRephrasingProject?.projectId || ''}
            draftRephrasingProject={draftRephrasingProject}
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            onChange={() => {
              debouncedUpdateOrCreateProject();
            }}
          />
        </RephraseContent>
      );
    }
    if (isLoading) {
      return <SimulatedProgressLoadingScreen />;
    }
    return <EmptyRephraseContent />;
  };

  const hideGenerateSection = useMemo(
    () =>
      !draftRephrasingProject.isInitialized ||
      validUploadedVideos.length === 0 ||
      !readyAsset,
    [draftRephrasingProject.isInitialized, validUploadedVideos, readyAsset]
  );

  return (
    <FeatureEditor
      feature={FeatureName.REPHRASING}
      featureDetailsProps={{
        featureDetailsComponent: getFeatureDetailsComponent(),
      }}
      renameProjectProps={{
        projectName: draftRephrasingProject?.projectName || '',
        onBlur: (name) => {
          updateOrCreateProject();
          uiInteraction({
            item_name: 'rename_project',
            interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE,
            flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
            interaction_details: JSON.stringify({
              project_name: name,
            }),
          });
        },
        projectNameChange: (name) => {
          contentLabActions.updateDraftRephrasingProjectName(name);
        },
        projectNamePlaceholder: translate('Name your Rescript project'),
      }}
      generateProjectProps={{
        description: generateProjectDescription,
        assetErrors,
        readyAssets,
        onGenerateClick: handleGenerateClick,
        creditsCost: getRequiredCredits(),
        disabled: isGenerateButtonDisabled,
        isLoading: isGenerateButtonLoading,
        tooltipText: creditsTooltipTextForFeature(
          FeatureName.REPHRASING,
          validUploadedVideos.length,
          0
        ),
      }}
      fileUploaderProps={{
        title: translate('Upload a video with speech'),
        guideTitle: translate('must_contain_clear_speech_with_limits', {
          maxDuration: translate(
            getUploadLimits(FeatureName.REPHRASING).maxDurationDisplayKey
          ),
          maxSize: translate(
            getUploadLimits(FeatureName.REPHRASING).maxFileSizeDisplayKey
          ),
        }),
        guideDescription: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.REPHRASING]
        ),
        shortenGuideText: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.REPHRASING]
        ),
        assetErrors: isLoading ? [] : assetErrors,
        renderUploadContent: draftRephrasingProject.transcriptionLoaded
          ? renderUploadContent
          : undefined,
        allowMultiUpload: false,
        onFilePickerReady,
        onFilesSelected,
        uploadMode: UploadMode.SINGLE,
        isLoading: isFileLoading,
        onLearnMoreClick,
      }}
      hideGenerateSection={hideGenerateSection}
    />
  );
}

export default RephrasePage;
